import { env, isDev } from '@/config/env';
import { User } from '@/types';
import { decodePermissions } from '@/utils/permissionHelpers';
import { serviceProviderUserService } from './serviceProviderUserService';

/**
 * Authentication API response interfaces
 */
export interface LoginCredentials {
  loginId: string;
  password: string;
  applicationId: string;
  tenantId: string;
}

export interface LoginResponse {
  user: User;
  expiresIn: number;
  // Production: tokens are set as HttpOnly cookies by server
  // Development: token may be included in response for Bearer auth
  token?: string;
  refreshToken?: string;
}

export interface AuthError {
  message: string;
  code?: string | undefined;
  details?: any;
}

/**
 * Minimal user data stored in localStorage for security
 * Contains only non-sensitive display information
 */
export interface MinimalUserData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;  // Email included (it's the username, not sensitive)
  // Roles are NOT stored in localStorage for security
}

/**
 * Custom error class for authentication errors
 */
export class AuthenticationError extends Error {
  public statusCode: number | undefined;
  public details?: AuthError | undefined;

  constructor(
    message: string,
    statusCode?: number,
    details?: AuthError
  ) {
    super(message);
    this.name = 'AuthenticationError';
    this.statusCode = statusCode;
    this.details = details;
  }
}

/**
 * localStorage key for the Permission JWT (Token #2). Exported so callers that must issue a
 * request outside authenticatedFetch can attach the same token instead of duplicating the key.
 */
export const PERMISSION_TOKEN_STORAGE_KEY = 'permissionToken';

/**
 * Simple authentication service using real API
 * Uses localStorage for token storage in development
 */
export class AuthService {
  // Two-token system localStorage keys
  private readonly FUSION_AUTH_TOKEN_KEY = 'fusionAuthToken';  // Token #1 (FusionAuth JWT)
  private readonly PERMISSION_TOKEN_KEY = PERMISSION_TOKEN_STORAGE_KEY;  // Token #2 (Permission JWT)
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';          // Refresh token for Token #2
  private readonly TOKEN_EXPIRES_AT_KEY = 'tokenExpiresAt';     // Expiration timestamp
  private readonly USER_INFO_KEY = 'userInfo';
  private readonly LAST_ACTIVITY_KEY = 'lastActivity';          // Track user activity

  // Legacy key for backward compatibility
  private readonly AUTH_TOKEN_KEY = 'authToken';

  // Prevent race conditions during token refresh
  private refreshPromise: Promise<void> | null = null;

  // Prevent race conditions during logout/cleanup
  private isLoggingOut: boolean = false;

  // Activity tracking
  private activityCheckInterval: NodeJS.Timeout | null = null;
  private activityHandler: (() => void) | null = null;
  private readonly activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

  constructor() {
    // Activity monitoring will start after successful login to avoid tracking before authentication
    
    // Check for expired tokens on initialization and properly logout if needed
    this.initializeAuthState();
  }

  /**
   * Initialize authentication state on page load/refresh
   * Properly logout if tokens are expired
   */
  private initializeAuthState(): void {
    const token = localStorage.getItem(this.PERMISSION_TOKEN_KEY);
    const expiresAt = localStorage.getItem(this.TOKEN_EXPIRES_AT_KEY);

    // Debug logging for inactivity timeout configuration
    console.debug('[AuthService] Initialization:', {
      inactivityTimeoutSeconds: env.inactivityTimeoutSeconds,
      activityCheckIntervalSeconds: env.activityCheckIntervalSeconds,
      tokenRefreshThresholdSeconds: env.tokenRefreshThresholdSeconds,
      tokenPresent: !!token,
      expiryTime: expiresAt
    });

    // If we have a token, check if it's expired
    if (token && expiresAt) {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();

      if (expiry <= now) {
        // Properly logout to invalidate refresh token on server
        // Use setTimeout to avoid blocking constructor
        setTimeout(() => {
          this.logout().catch(() => {
            // If logout fails, at least clear local data
            this.clearAuthData();
          });
        }, 0);
      } else {
        // Token is still valid - restart activity monitoring
        console.debug('[AuthService] Token valid, starting activity monitoring');
        this.startActivityMonitoring();
      }
    }
  }

  /**
   * Track user activity for intelligent token refresh
   */
  private updateLastActivity(): void {
    const now = Date.now();
    localStorage.setItem(this.LAST_ACTIVITY_KEY, now.toString());
  }

  /**
   * Check if user has been inactive for too long
   */
  private isUserInactive(): boolean {
    const lastActivityStr = localStorage.getItem(this.LAST_ACTIVITY_KEY);

    if (!lastActivityStr) {
      console.warn('[AuthService] No activity timestamp found in localStorage');
      return true; // No activity recorded = inactive
    }

    const lastActivity = parseInt(lastActivityStr);
    const now = Date.now();
    const inactiveTime = (now - lastActivity) / 1000; // seconds
    const isInactive = inactiveTime > env.inactivityTimeoutSeconds;

    // Only log if approaching timeout or already timed out
    if (inactiveTime > (env.inactivityTimeoutSeconds * 0.8)) {
      console.debug('[AuthService] Activity check:', {
        inactivityTimeoutSeconds: env.inactivityTimeoutSeconds,
        inactiveTimeSeconds: Math.round(inactiveTime),
        isInactive,
        lastActivityTime: new Date(lastActivity).toISOString(),
        currentTime: new Date(now).toISOString()
      });
    }

    return isInactive;
  }

  /**
   * Start monitoring user activity and proactively refresh tokens
   * Called after successful login
   */
  private startActivityMonitoring(): void {
    // Prevent duplicate initialization
    if (this.activityHandler || this.activityCheckInterval) {
      return;
    }

    console.debug('[AuthService] Starting activity monitoring', {
      inactivityTimeoutSeconds: env.inactivityTimeoutSeconds,
      activityCheckIntervalSeconds: env.activityCheckIntervalSeconds,
      tokenRefreshThresholdSeconds: env.tokenRefreshThresholdSeconds,
      inactivityTimeoutMinutes: Math.round(env.inactivityTimeoutSeconds / 60),
      checkIntervalMilliseconds: env.activityCheckIntervalSeconds * 1000
    });

    // Store handler reference for cleanup
    this.activityHandler = () => {
      this.updateLastActivity();
    };

    // Track activity on common user interactions
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.activityHandler!, { passive: true });
    });

    // Set initial activity
    this.updateLastActivity();

    // Check every configured interval for proactive refresh
    this.activityCheckInterval = setInterval(() => {
      this.checkForProactiveRefresh();
    }, env.activityCheckIntervalSeconds * 1000);
  }

  /**
   * Stop monitoring user activity and clean up event listeners
   * Called during logout/cleanup
   */
  private stopActivityMonitoring(): void {
    // Remove event listeners
    if (this.activityHandler) {
      this.activityEvents.forEach(event => {
        document.removeEventListener(event, this.activityHandler!);
      });
      this.activityHandler = null;
    }

    // Clear interval
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
    }
  }

  /**
   * Proactively refresh token if user is active and token expires soon
   */
  private async checkForProactiveRefresh(): Promise<void> {
    try {
      if (!this.isAuthenticated()) {
        return;
      }

      const isInactive = this.isUserInactive();
      const lastActivityStr = localStorage.getItem(this.LAST_ACTIVITY_KEY);
      const lastActivity = lastActivityStr ? parseInt(lastActivityStr) : null;
      const now = Date.now();
      const inactiveTimeSeconds = lastActivity ? (now - lastActivity) / 1000 : null;

      // Debug logging - inactivity check
      if (isInactive) {
        console.warn('[AuthService] User inactive - logging out', {
          inactivityTimeoutSeconds: env.inactivityTimeoutSeconds,
          inactiveTimeSeconds: Math.round(inactiveTimeSeconds || 0),
          lastActivityTimestamp: lastActivity,
          currentTimestamp: now
        });
      }

      if (isInactive) {
        await this.logout();
        return;
      }

      const expiresAt = localStorage.getItem(this.TOKEN_EXPIRES_AT_KEY);
      if (!expiresAt) {
        console.warn('Proactive refresh skipped: No token expiration data');
        return;
      }

      const expiry = new Date(expiresAt).getTime();
      const timeUntilExpiry = Math.floor((expiry - now) / 1000);

      // Check if token is already expired
      if (timeUntilExpiry <= 0) {
        console.warn('Proactive refresh skipped: Token already expired');
        return;
      }

      // If user is active and token expires within threshold, refresh it
      if (timeUntilExpiry <= env.tokenRefreshThresholdSeconds) {
        console.debug('[AuthService] Token expiring soon - refreshing', { timeUntilExpiry, threshold: env.tokenRefreshThresholdSeconds });
        await this.refreshPermissionToken();
      }
    } catch (error) {
      // Proactive refresh failed - silently continue
    }
  }

  /**
   * Exchange FusionAuth token (Token #1) for Permission token (Token #2)
   * @param fusionAuthToken - JWT token from FusionAuth login
   * @returns Permission token data from Authorization API
   */
  private async exchangeForPermissionToken(fusionAuthToken: string): Promise<{
    token: string;
    refreshToken: string;
    expiresAt: string;
    refreshTokenExpiresAt: string;
    userId: string;
    permissionCount: number;
  }> {
    const exchangeEndpoint = `${env.authorizationServiceUrl}/authorization/token`;

    const response = await fetch(exchangeEndpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${fusionAuthToken}`,
        'Content-Type': 'application/json'
      },
      signal: AbortSignal.timeout(15000)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new AuthenticationError(
        errorData.message || 'Failed to exchange token for permissions',
        response.status
      );
    }

    const data = await response.json();
    return data;
  }

  /**
   * Authenticates user with email and password
   * Performs two-step authentication:
   * 1. Gets Token #1 from FusionAuth
   * 2. Exchanges Token #1 for Token #2 (Permission JWT) from Authorization API
   * @param email - User's email address
   * @param password - User's password
   * @returns Promise with authentication result
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      // Use direct endpoint to FusionAuth API
      const loginEndpoint = `${env.authServiceUrl}/api/login`;

      // Get tenantId from environment configuration
      const tenantId = env.authTenantId;

      const loginPayload: LoginCredentials = {
        loginId: email,
        password: password,
        applicationId: env.authClientId,
        tenantId: tenantId,
      };

      const response = await fetch(loginEndpoint, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-FusionAuth-TenantId': tenantId,
        },
        body: JSON.stringify(loginPayload),
        credentials: 'include', // Essential for cookies
        signal: AbortSignal.timeout(30000), // 30 second timeout
      });

      if (!response.ok || response.status === 202) {
        const rawErrorData = await response.json().catch(() => ({}));
        
        // Ensure errorData conforms to AuthError interface
        const errorData: AuthError = {
          message: typeof rawErrorData === 'object' && rawErrorData && 'message' in rawErrorData 
            ? String(rawErrorData.message) 
            : `HTTP ${response.status} error`,
          code: typeof rawErrorData === 'object' && rawErrorData && 'code' in rawErrorData 
            ? String(rawErrorData.code) 
            : undefined,
          details: rawErrorData
        };

        // Build a user-friendly error message based on HTTP status code
        let errorMessage: string;
        if (errorData.message && errorData.message !== `HTTP ${response.status} error`) {
          errorMessage = errorData.message;
        } else {
          switch (response.status) {
            case 202:
              errorMessage = 'You are not authorized to access this application. Please contact your administrator.';
              break;
            case 400:
              errorMessage = 'Invalid request. Please check your input and try again.';
              break;
            case 401:
              errorMessage = 'Invalid credentials. Please check your email and password.';
              break;
            case 403:
              errorMessage = 'Access forbidden. You do not have permission to log in.';
              break;
            case 429:
              errorMessage = 'Too many login attempts. Please try again later.';
              break;
            case 500:
            case 502:
            case 503:
              errorMessage = 'Server error. Please try again later.';
              break;
            default:
              errorMessage = `Authentication failed (status: ${response.status}).`;
          }
        }

        throw new AuthenticationError(
          errorMessage,
          response.status,
          errorData
        );
      }

      const fusionAuthResult: LoginResponse = await response.json();

      // STEP 2: Store Token #1 and exchange for Token #2
      const fusionAuthToken = fusionAuthResult.token;

      if (!fusionAuthToken) {
        throw new AuthenticationError('FusionAuth did not return a token');
      }

      // Store Token #1 in localStorage
      localStorage.setItem(this.FUSION_AUTH_TOKEN_KEY, fusionAuthToken);

      try {
        // STEP 3: Exchange Token #1 for Token #2
        const permissionTokenData = await this.exchangeForPermissionToken(fusionAuthToken);

        // STEP 4: Store Token #2, refresh token, and user data in localStorage
        this.storePermissionTokenData(fusionAuthResult, permissionTokenData);
        
        // Initialize activity tracking and monitoring
        this.updateLastActivity();
        this.startActivityMonitoring();

        // Calculate actual expiration time from backend response
        const now = new Date().getTime();
        const expiry = new Date(permissionTokenData.expiresAt).getTime();
        const actualExpiresIn = Math.floor((expiry - now) / 1000);

        return {
          user: fusionAuthResult.user,
          expiresIn: actualExpiresIn,
          token: permissionTokenData.token,
          refreshToken: permissionTokenData.refreshToken
        };
      } catch (exchangeError) {
        // Token exchange failed - clear everything and show error
        this.clearAuthData();
        throw new AuthenticationError(
          'Failed to obtain permissions. Please try again.',
          undefined,
          { message: exchangeError instanceof Error ? exchangeError.message : 'Token exchange failed' }
        );
      }
    } catch (error) {
      if (error instanceof AuthenticationError) {
        throw error;
      }

      if (error instanceof Error) {
        throw new AuthenticationError(
          error.message || 'Authentication failed. Please try again.',
          undefined,
          { message: error.message }
        );
      }

      throw new AuthenticationError('An unknown authentication error occurred');
    }
  }

  /**
   * Refresh Permission Token (Token #2) using refresh token from localStorage
   * Implements token rotation - old refresh token invalidated, new one issued
   */
  async refreshPermissionToken(): Promise<void> {
    // If refresh is already in progress, wait for it instead of starting a new one
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Create refresh promise
    this.refreshPromise = (async () => {
      try {
        const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);

        if (!refreshToken) {
          throw new AuthenticationError('No refresh token available');
        }

        const refreshEndpoint = `${env.authorizationServiceUrl}/authorization/refresh-token`;

        const response = await fetch(refreshEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ refreshToken }),
          signal: AbortSignal.timeout(15000)
        });
        
        if (!response.ok) {
          await response.json().catch(() => ({}));
          
          if (!this.isLoggingOut) {
            await this.logout();
          }
          throw new AuthenticationError('Token refresh failed, please login again', response.status);
        }

        const data = await response.json();

        // Handle field name difference: login uses 'token', refresh uses 'accessToken'
        const newToken = data.token || data.accessToken;
        const newRefreshToken = data.refreshToken;
        const newExpiresAt = data.expiresAt;
        
        if (!newToken) {
          throw new AuthenticationError('Refresh response missing token');
        }

        if (!newRefreshToken || !newExpiresAt) {
          throw new AuthenticationError('Refresh response missing required fields (refreshToken or expiresAt)');
        }

        // Update localStorage with NEW Token #2 and refresh token (rotation)
        localStorage.setItem(this.PERMISSION_TOKEN_KEY, newToken);
        localStorage.setItem(this.REFRESH_TOKEN_KEY, newRefreshToken);
        localStorage.setItem(this.TOKEN_EXPIRES_AT_KEY, newExpiresAt);

        // Sync refreshed permissions into Redux so UI gating stays current
        const { store } = await import('@/store');
        const { updateUser } = await import('@/store/slices/authSlice');
        const permissions = decodePermissions(newToken);
        store.dispatch(updateUser({ permissions }));
      } catch (error) {
        // Handle all errors (network, timeout, etc.)
        if (!this.isLoggingOut) {
          await this.logout();
        }
        throw error;
      } finally {
        // Clear refresh promise when done (success or failure)
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Get valid Permission Token (Token #2) from localStorage
   * Automatically refreshes if expiring within configured threshold
   * @returns Valid Permission Token
   */
  async getValidPermissionToken(): Promise<string> {
    const token = localStorage.getItem(this.PERMISSION_TOKEN_KEY);
    const expiresAt = localStorage.getItem(this.TOKEN_EXPIRES_AT_KEY);

    if (!token) {
      if (!this.isLoggingOut) {
        await this.logout();
      }
      throw new AuthenticationError('No permission token available');
    }

    if (!expiresAt) {
      if (!this.isLoggingOut) {
        await this.logout();
      }
      throw new AuthenticationError('No token expiration data');
    }

    // Check if token expires soon (configurable threshold)
    const now = new Date().getTime();
    const expiry = new Date(expiresAt).getTime();
    const timeUntilExpiry = Math.floor((expiry - now) / 1000);

    // Check if token is already expired
    if (expiry <= now) {
      if (!this.isLoggingOut) {
        await this.logout();
      }
      throw new AuthenticationError('Token expired');
    }

    if (timeUntilExpiry <= env.tokenRefreshThresholdSeconds) {
      try {
        await this.refreshPermissionToken();
        const newToken = localStorage.getItem(this.PERMISSION_TOKEN_KEY);
        return newToken!;
      } catch (error) {
        // Refresh failed - already redirected to login
        throw error;
      }
    }

    return token;
  }

  /**
   * Logs out the current user
   * Invalidates refresh token on Authorization API and clears all local data
   */
  async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);

      // Invalidate Permission Token (Token #2) refresh token on Authorization API
      if (refreshToken) {
        const authLogoutEndpoint = `${env.authorizationServiceUrl}/authorization/logout`;

        try {
          const response = await fetch(authLogoutEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ refreshToken }),
            signal: AbortSignal.timeout(10000)
          });

          if (!response.ok) {
            await response.json().catch(() => ({}));
            // Authorization logout failed - continue with cleanup
          }
        } catch (error) {
          // Network error during logout - continue with cleanup
        }
      } else {
        // No refresh token to invalidate
      }

      // Call FusionAuth logout endpoint - clears HttpOnly cookies
      const fusionAuthLogoutEndpoint = `${env.authServiceUrl}/api/logout`;

      await fetch(fusionAuthLogoutEndpoint, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: AbortSignal.timeout(10000)
      }).catch(() => {
        // Silently handle logout API errors
      });

      // Always clear ALL authentication data
      this.clearAuthData();
      
      // Redirect to login page
      window.location.href = '/login';
    } catch (error) {
      // Still clear client data even if API fails
      this.clearAuthData();
      window.location.href = '/login';
    }
  }

  /**
   * Refreshes the authentication token
   * Server manages HttpOnly refresh token automatically
   * @returns Promise with new authentication data
   */
  async refreshToken(): Promise<LoginResponse> {
    try {
      const refreshEndpoint = `${env.authServiceUrl}/api/refresh`;

      const response = await fetch(refreshEndpoint, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include HttpOnly refresh token
        signal: AbortSignal.timeout(15000), // 15 second timeout
      });

      if (!response.ok) {
        const rawErrorData = await response.json().catch(() => ({}));
        
        // Ensure errorData conforms to AuthError interface
        const errorData: AuthError = {
          message: typeof rawErrorData === 'object' && rawErrorData && 'message' in rawErrorData 
            ? String(rawErrorData.message) 
            : 'Token refresh failed',
          code: typeof rawErrorData === 'object' && rawErrorData && 'code' in rawErrorData 
            ? String(rawErrorData.code) 
            : undefined,
          details: rawErrorData
        };
        
        throw new AuthenticationError(
          'Token refresh failed',
          response.status,
          errorData
        );
      }

      const authResult: LoginResponse = await response.json();

      // Update stored authentication data
      this.storeAuthData(authResult);

      return authResult;
    } catch (error) {
      // Clear invalid session
      this.clearAuthData();

      if (error instanceof AuthenticationError) {
        throw error;
      }

      throw new AuthenticationError('Token refresh failed');
    }
  }

  /**
   * Stores authentication data in localStorage.
   *
   * PRODUCTION MODE: Uses HttpOnly cookies for secure token storage.
   * Only stores a session indicator and minimal user data in localStorage.
   *
   * DEVELOPMENT MODE: May store actual JWT token for Bearer authentication
   * if the API returns a token field, otherwise falls back to cookie-only mode.
   *
   * MINIMAL USER DATA: Only stores non-sensitive user data (ID, first/last name) in localStorage.
   * Sensitive data like email, roles, and permissions are fetched from the server when needed
   * using proper authentication, protecting against XSS attacks.
   *
   * This approach is secure because:
   * 1. Production always uses HttpOnly cookies (immune to XSS)
   * 2. Development tokens are only used when explicitly provided by API
   * 3. User data in localStorage contains no sensitive information
   * 4. Complete user profile requires server authentication
   */
  private storeAuthData(authData: LoginResponse): void {
    // Handle token storage based on environment and API response
    if (isDev && authData.token) {
      // Development: Store actual token if provided by API for Bearer auth
      localStorage.setItem(this.AUTH_TOKEN_KEY, authData.token);
    } else {
      // Production or cookie-only development: Store session indicator
      const sessionIndicator = isDev
        ? `dev_session_${Date.now()}`
        : `session_${Date.now()}`;
      localStorage.setItem(this.AUTH_TOKEN_KEY, sessionIndicator);
    }

    // Store only minimal, non-sensitive user data for display purposes
    if (authData.user) {
      const minimalUserData: MinimalUserData = {
        id: authData.user.id,
        firstName: authData.user.firstName,
        lastName: authData.user.lastName,
        email: authData.user.email || ''
        // Note: roles and other sensitive data are NOT stored
      };
      localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(minimalUserData));
    }
  }

  /**
   * Fetch full user profile from User Service API
   * @param userId - User ID extracted from permission token
   * @returns User profile data from User Service
   */
  async fetchUserProfile(userId: string): Promise<{
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    userRoles?: string[];
    userType: string;
    mobileNumber?: string;
    serviceProviderId?: string;
    authorizedStates?: string[];
    authorizedDistricts?: string[];
  }> {
    try {
      const userProfile = await serviceProviderUserService.getUserById(userId);
      return userProfile as {
        id: string;
        email: string;
        firstName?: string;
        lastName?: string;
        userRoles?: string[];
        userType: string;
        mobileNumber?: string;
        serviceProviderId?: string;
        authorizedStates?: string[];
        authorizedDistricts?: string[];
      };
    } catch (error) {
      console.error('❌ [AuthService] Failed to fetch user profile:', error);
      throw new AuthenticationError(
        'Failed to fetch user profile',
        undefined,
        { message: error instanceof Error ? error.message : 'User profile fetch failed' }
      );
    }
  }

  /**
   * Store Permission token (Token #2), refresh token, and user data in localStorage
   * Token #1 should already be stored before calling this method
   */
  private storePermissionTokenData(
    fusionAuthResult: LoginResponse,
    permissionTokenData: {
      token: string;
      refreshToken: string;
      expiresAt: string;
      refreshTokenExpiresAt: string;
      userId: string;
      permissionCount: number;
    }
  ): void {
    // Store Token #2 (Permission JWT) - used for all API requests
    localStorage.setItem(this.PERMISSION_TOKEN_KEY, permissionTokenData.token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, permissionTokenData.refreshToken);
    localStorage.setItem(this.TOKEN_EXPIRES_AT_KEY, permissionTokenData.expiresAt);

    // Store minimal user data (including email for display)
    const minimalUserData: MinimalUserData = {
      id: fusionAuthResult.user.id,
      firstName: fusionAuthResult.user.firstName,
      lastName: fusionAuthResult.user.lastName,
      email: fusionAuthResult.user.email || ''
    };
    localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(minimalUserData));
  }

  /**
   * Clears localStorage authentication data and stops activity monitoring
   */
  private clearAuthData(): void {
    // Reset logging out flag at the start of cleanup
    this.isLoggingOut = false;
    
    // Clear two-token system keys
    localStorage.removeItem(this.FUSION_AUTH_TOKEN_KEY);
    localStorage.removeItem(this.PERMISSION_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRES_AT_KEY);
    localStorage.removeItem(this.USER_INFO_KEY);
    localStorage.removeItem(this.LAST_ACTIVITY_KEY);

    // Clear legacy key for backward compatibility
    localStorage.removeItem(this.AUTH_TOKEN_KEY);

    // Stop activity monitoring and clean up event listeners
    this.stopActivityMonitoring();
  }

  /**
   * Gets minimal user information from localStorage
   * For complete user profile including sensitive data, use getUserProfile()
   */
  getCurrentUser(): MinimalUserData | null {
    const userJson = localStorage.getItem(this.USER_INFO_KEY);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson) as MinimalUserData;
    } catch {
      return null;
    }
  }

  /**
   * Fetches complete user profile from FusionAuth using HttpOnly cookies
   * FusionAuth authenticates via the access_token cookie it sets during login
   * This includes sensitive data like email, roles, permissions, etc.
   * @returns Promise with complete user profile or null if not authenticated
   */
  async getUserProfile(): Promise<User | null> {
    if (!this.isAuthenticated()) {
      return null;
    }

    try {
      const profileEndpoint = `${env.authServiceUrl}/api/user/profile`;

      const response = await fetch(profileEndpoint, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // FusionAuth authenticates via HttpOnly cookies
        signal: AbortSignal.timeout(15000),
      });

      if (!response.ok) {
        // If unauthorized, just return null without logging out
        // The main permission token may still be valid
        if (response.status === 401) {
          // Profile request unauthorized but user session remains valid
        }
        return null;
      }

      const userProfile: User = await response.json();
      return userProfile;
    } catch (error) {
      // Don't expose server errors, just return null
      return null;
    }
  }

  /**
   * Checks if user is currently authenticated
   * Checks for Permission Token (Token #2) in localStorage and validates expiration
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem(this.PERMISSION_TOKEN_KEY);
    const user = this.getCurrentUser();
    const expiresAt = localStorage.getItem(this.TOKEN_EXPIRES_AT_KEY);

    if (!token || !user) {
      return false;
    }

    // Check if token is expired
    if (expiresAt) {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();

      if (expiry <= now) {
        // Token expired - clear and return false
        this.clearAuthData();
        return false;
      }
    }

    return true;
  }

  /**
   * Gets the Permission Token (Token #2) from localStorage
   * Returns null if token is expired
   * @returns Permission JWT token or null
   */
  getToken(): string | null {
    const token = localStorage.getItem(this.PERMISSION_TOKEN_KEY);
    const expiresAt = localStorage.getItem(this.TOKEN_EXPIRES_AT_KEY);

    // Return null if no token
    if (!token) {
      return null;
    }

    // Validate token is not expired
    if (expiresAt) {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();

      if (expiry <= now) {
        // Token expired - return null
        return null;
      }
    }

    return token;
  }

  /**
   * Gets the FusionAuth Token (Token #1) from localStorage
   * @returns FusionAuth JWT token or null
   */
  getFusionAuthToken(): string | null {
    return localStorage.getItem(this.FUSION_AUTH_TOKEN_KEY);
  }

  /**
   * Gets authorization headers for API requests
   * Returns Permission Token (Token #2) from localStorage in Bearer format
   * 
   * Note: Returns empty object if token is null/expired. Callers should check
   * for empty Authorization header or use authenticatedFetch() which handles
   * token validation, refresh, and retry logic automatically.
   * 
   * @returns Object with Authorization header if token exists and is valid, empty object otherwise
   */
  getAuthHeader(): Record<string, string> {
    const token = this.getToken();  // Gets Token #2 from localStorage (returns null if expired)

    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    }

    return {};
  }

  /**
   * Makes an authenticated API request with Permission Token (Token #2) from localStorage
   * Automatically refreshes token if expiring within configured threshold
   * Tracks user activity for intelligent refresh decisions
   * Retries once on 401 after refreshing token
   */
  async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    // Get valid Permission Token from localStorage (auto-refreshes if needed)
    const token = await this.getValidPermissionToken();

    if (!token) {
      if (!this.isLoggingOut) {
        await this.logout();
      }
      throw new AuthenticationError('No valid token available');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {}),
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    // Handle 401 (Unauthorized) by attempting refresh and retry
    // Note: 403 (Forbidden) and other non-401 authentication-related responses
    // are intentionally passed through to the caller. A 403 typically indicates
    // that the user is authenticated but not authorized for the requested
    // resource, so feature-level code should decide how to handle it (e.g.
    // showing an "access denied" message or redirecting to a dedicated page).
    if (response.status === 401) {
      try {
        await this.refreshPermissionToken();
        const newToken = localStorage.getItem(this.PERMISSION_TOKEN_KEY);

        if (!newToken) {
          throw new AuthenticationError('Token refresh did not provide new token');
        }

        // Retry request with new token from localStorage
        const retryResponse = await fetch(url, {
          ...options,
          headers: {
            ...headers,
            'Authorization': `Bearer ${newToken}`
          }
        });

        // If retry also returns 401, session is invalid
        if (retryResponse.status === 401) {
          if (!this.isLoggingOut) {
            await this.logout();
          }
          throw new AuthenticationError('Session expired, please login again');
        }

        // Don't track activity on API calls - only user interactions should count
        return retryResponse;
      } catch (error) {
        // Refresh failed - logout and redirect
        if (!this.isLoggingOut) {
          await this.logout();
        }
        throw new AuthenticationError('Session expired, please login again');
      }
    }

    // Don't track activity on API calls - only user interactions should count
    return response;
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export default as the service instance
export default authService;